import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Home, RotateCcw, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { GooseBerryOutput } from "@/components/GooseBerryOutput";
import { ArtifactUploadPanel } from "@/views/gooseberry/ArtifactUploadPanel";
import { MethodAndModePanel } from "@/views/gooseberry/MethodAndModePanel";
import { ContextPanel } from "@/views/gooseberry/ContextPanel";
import { CustomDatabasePanel } from "@/views/gooseberry/CustomDatabasePanel";
import { ResearchMetadataPanel } from "@/views/gooseberry/ResearchMetadataPanel";
import { DatasetPanel } from "@/views/gooseberry/DatasetPanel";
import { ModePanel } from "@/views/gooseberry/ModePanel";
import { RunPanel } from "@/views/gooseberry/RunPanel";
import { useGooseBerryForm } from "@/views/gooseberry/useGooseBerryForm";
import { useGooseBerryFeedback } from "@/views/gooseberry/useGooseBerryFeedback";
import { analysisService, researchMetadataService, feedbackService } from "@/lib/api";
import { scrollToElement, scrollToTop } from "@/lib/scroll";
import { exportGooseBerryPdf } from "@/lib/pdf";
import { logger } from "@/lib/logger";

// ---- Local helpers (pure functions, kept out of the component for readability) ----
const PUBLIC_METADATA_FALLBACK = [
  {
    source: "ODISSEI",
    title: "ODISSEI Portal, Open Data Infrastructure for Social Science",
    description: "Public metadata catalog for Dutch social-science datasets. Used as metadata grounding only.",
    url: "https://portal.odissei.nl/",
    variables: ["demographics", "labor", "education", "health", "household composition"],
    accessNote: "Metadata only; raw datasets are governed by individual data providers.",
    sourceStatus: "curated",
  },
];

function publicMetadataFallback(language = "en") {
  if (language !== "nl") return PUBLIC_METADATA_FALLBACK;
  return PUBLIC_METADATA_FALLBACK.map((item) => ({
    ...item,
    description: "Publieke metadatacatalogus voor Nederlandse sociaalwetenschappelijke datasets. Alleen gebruikt als metadata-onderbouwing.",
    accessNote: "Alleen metadata; raw datasets vallen onder de voorwaarden van individuele dataproviders.",
  }));
}

function newRunId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `run-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function extractB64(dataUrl) {
  if (!dataUrl) return "";
  return dataUrl.includes(";base64,") ? dataUrl.split(";base64,")[1] : dataUrl;
}

// Build the payload for /api/analyze-design. Keeping this OUT of the component
// + memoized means the "Run" callback only depends on the bundle (1 reference)
// instead of every individual form field (16 references).
function buildAnalysisPayload({ form, derived, image, research, language }) {
  return {
    projectName: form.projectName,
    targetGroup: form.targetGroup,
    societalTheme: form.societalTheme,
    designPhase: form.designPhase,
    assumptions: form.assumptions,
    fidelity: derived.fidelityLabel,
    selectedMethod: derived.selectedMethod,
    customDatabaseText: form.customText,
    customDatabaseFileName: form.customFileName,
    customDatabaseFileContent: form.customFileContent,
    customDatabaseType: form.customType,
    researchMetadata: research,
    language,
    mode: form.mode,
    image: extractB64(image.dataUrl),
    imageMime: image.mime || "image/png",
  };
}

// Map an API error response onto a user-facing toast. Returns nothing, the
// side-effect IS the toast. Extracted so onRun and onRegenerate share the
// same logic.
function reportApiError(err, fallbackTitle, language = "en") {
  const nl = language === "nl";
  logger.error(fallbackTitle, err);
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const friendlyMsg =
    (detail && typeof detail === "object" && detail.message) ||
    (typeof detail === "string" && detail) ||
    null;
  if (status === 415) {
    toast.error(nl ? "Afbeelding wordt niet ondersteund" : "Image not supported", {
      description:
        friendlyMsg || (nl ? "Je afbeelding kon niet worden verwerkt. Probeer een standaard JPEG of PNG." : "Your image could not be processed. Try a standard JPEG or PNG."),
      duration: 8000,
    });
  } else if (status === 402) {
    toast.error(nl ? "Analysecredit is op" : "Analysis credit exhausted", {
      description:
        friendlyMsg ||
        (nl ? "De ingestelde OpenAI/LLM-key heeft geen credit meer. Controleer de backend secret in Fly.io." : "The configured OpenAI/LLM key is out of credit. Check the backend secret in Fly.io."),
      duration: 12000,
    });
  } else if (status === 503) {
    toast.error(nl ? "Analyse-assistant niet beschikbaar" : "Analysis assistant unavailable", {
      description:
        friendlyMsg ||
        (nl ? "De assistant is tijdelijk niet beschikbaar. Wacht even en druk opnieuw op Run simulatie." : "The assistant is temporarily unavailable. Please wait a moment and press Run simulation again."),
      duration: 8000,
    });
  } else if (status === 400) {
    toast.error(nl ? "Kan nog niet starten" : "Cannot run yet", {
      description:
        friendlyMsg || (nl ? "Vul de verplichte velden in en upload een ontwerpafbeelding." : "Please fill in the required fields and upload a design image."),
    });
  } else {
    toast.error(fallbackTitle, { description: nl ? "Probeer het over een moment opnieuw." : "Please try again in a moment." });
  }
}

// ---- Component ----

export function GooseBerryView({ onGoHome, onLanguageChange }) {
  // All persistent form state lives in one hook (down from 13 useStates + 13 useEffects).
  const { form, setField, resetAll, derived } = useGooseBerryForm();

  // Image state, NOT persisted on purpose.
  const [imageDataUrl, setImageDataUrl] = useState("");
  const [imageMime, setImageMime] = useState("");

  // Research metadata (informational only)
  const [metadataResults, setMetadataResults] = useState([]);
  const [metadataStatus, setMetadataStatus] = useState({ ODISSEI: "curated" });
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [language, setLanguage] = useState("nl");

  // Output
  const [output, setOutput] = useState("");
  const [sourcesUsed, setSourcesUsed] = useState([]);
  const [visionUsed, setVisionUsed] = useState(true);
  const [modelInfo, setModelInfo] = useState({});
  const [groundingInfo, setGroundingInfo] = useState({});
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const outputRef = useRef(null);

  // Per-section feedback (extracted hook owns the state + derivations).
  const {
    handleFeedbackSubmitted,
    getFeedbackFor,
    correctionCount,
    compiledCorrections,
    resetFeedback,
  } = useGooseBerryFeedback();
  const [runId, setRunId] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const lastPayloadRef = useRef(null);

  // Memoized refs so onRun has stable deps. (`form` and `derived` are stable
  // objects whose REFERENCE only changes when their content changes, see
  // useGooseBerryForm.)
  const formRef = useRef(form);
  const derivedRef = useRef(derived);
  const imageRef = useRef({ dataUrl: imageDataUrl, mime: imageMime });
  const metadataRef = useRef({ results: metadataResults, status: metadataStatus });
  const selectedDatasetsRef = useRef(selectedDatasets);
  useEffect(() => { formRef.current = form; }, [form]);
  useEffect(() => { derivedRef.current = derived; }, [derived]);
  useEffect(() => { imageRef.current = { dataUrl: imageDataUrl, mime: imageMime }; }, [imageDataUrl, imageMime]);
  useEffect(() => { metadataRef.current = { results: metadataResults, status: metadataStatus }; }, [metadataResults, metadataStatus]);
  useEffect(() => { selectedDatasetsRef.current = selectedDatasets; }, [selectedDatasets]);

  // ---- Research metadata refresh ----
  const refreshMetadata = useCallback(async () => {
    setMetadataLoading(true);
    try {
      const q = [form.projectName, form.targetGroup, form.societalTheme].filter(Boolean).join(" ");
      const data = await researchMetadataService.search(q);
      setMetadataResults(data.results || []);
      setMetadataStatus(data.sourceStatus || { ODISSEI: "curated" });
    } catch (err) {
      logger.error("Research metadata fetch failed", err);
      toast(language === "nl" ? "Publieke metadatafallback wordt gebruikt" : "Using public metadata fallback", {
        description: language === "nl"
          ? "Live metadata kon niet worden geladen, maar GooseBerry kan nog steeds lokale codebooks en publieke fallback metadata gebruiken."
          : "Live metadata could not be loaded, but GooseBerry can still use local codebooks and curated public metadata.",
      });
      setMetadataResults(publicMetadataFallback(language));
      setMetadataStatus({ ODISSEI: "curated" });
    } finally {
      setMetadataLoading(false);
    }
  }, [form.projectName, form.targetGroup, form.societalTheme, language]);

  // Initial metadata fetch on mount only
  useEffect(() => {
    refreshMetadata();
    // We intentionally run this ONCE on mount; refreshMetadata is exposed for the Refresh button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canRun = !!imageDataUrl && !running;

  useEffect(() => {
    onLanguageChange?.(language);
  }, [language, onLanguageChange]);

  // ---- Run simulation ----
  // Reads everything via refs so the dep array stays at zero, eliminates the
  // 16-dependency complexity issue flagged by Code Review 3.
  const onRun = useCallback(async () => {
    const image = imageRef.current;
    if (!image.dataUrl) {
      toast.error(language === "nl" ? "Upload eerst een ontwerpconcept" : "Please upload a design concept first");
      return;
    }
    setRunning(true);
    setProgress(10);
    scrollToElement(outputRef.current, { delay: 80, offset: 90 });

    try {
      const f = formRef.current;
      const d = derivedRef.current;
      const autoQuery = [f.projectName, f.targetGroup, f.societalTheme, f.designPhase, d.selectedMethod?.name]
        .filter(Boolean)
        .join(" ");
      setProgress(25);

      // Try a fresh metadata pull; fall back silently to the current snapshot.
      let research = metadataRef.current.results;
      try {
        const data = await researchMetadataService.search(autoQuery || "");
        research = data.results || [];
        setMetadataResults(research);
        setMetadataStatus(data.sourceStatus || metadataRef.current.status);
      } catch (innerErr) {
        logger.warn("Research refresh during run failed", innerErr?.message);
        if (!research || research.length === 0) {
          research = publicMetadataFallback(language);
          setMetadataResults(research);
          setMetadataStatus({ ODISSEI: "curated" });
        }
      }
      setProgress(55);

        const payload = {
            ...buildAnalysisPayload({ form: f, derived: d, image, research, language }),
            selectedDatasets: selectedDatasetsRef.current,
        };
        setProgress(70);
        const data = await analysisService.generateDesignAnalysis(payload);

      setOutput(data.markdown || "");
      setSourcesUsed(data.sourcesUsed || research || []);
      setVisionUsed(data.visionUsed !== false);
      setModelInfo(data.modelInfo || {});
      setGroundingInfo(data.groundingInfo || {});
      if (data.visionUsed === false) {
        toast(language === "nl" ? "Afbeeldingsanalyse is overgeslagen" : "Image analysis was skipped", {
          description:
            language === "nl"
              ? "De assistant kon je afbeelding niet analyseren, daarom is het rapport alleen gebaseerd op de tekstuele context."
              : "The assistant could not analyze your image, so the report is based on the textual context only.",
          duration: 8000,
        });
      }
      setRunId(newRunId());
      resetFeedback();
      lastPayloadRef.current = payload;
      setProgress(100);
      scrollToElement(outputRef.current, { delay: 60, offset: 90 });
    } catch (err) {
      reportApiError(err, language === "nl" ? "Analyse mislukt" : "Analysis failed", language);
    } finally {
      setRunning(false);
      setTimeout(() => setProgress(0), 600);
    }
  }, [resetFeedback, language]);

  // ---- Image handlers ----
  const onImageChange = useCallback(({ dataUrl, mime }) => {
    setImageDataUrl(dataUrl);
    setImageMime(mime);
  }, []);
  const onImageReset = useCallback(() => {
    setImageDataUrl("");
    setImageMime("");
  }, []);

  // ---- Clear all ----
  const clearAll = useCallback(() => {
    resetAll();
    setOutput("");
    setSourcesUsed([]);
    setVisionUsed(true);
    setModelInfo({});
    setGroundingInfo({});
    setLanguage("nl");
    setRunId("");
    resetFeedback();
    lastPayloadRef.current = null;
    onImageReset();
    scrollToTop({ delay: 30 });
    toast(language === "nl" ? "GooseBerry-input gewist" : "GooseBerry inputs cleared");
  }, [resetAll, resetFeedback, onImageReset, language]);

  // ---- Output actions ----
  const copyMarkdown = useCallback(async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      toast(language === "nl" ? "Markdown gekopieerd" : "Markdown copied");
    } catch (err) {
      logger.warn("Clipboard write failed", err?.message);
      toast.error(language === "nl" ? "Kopieren mislukt" : "Copy failed");
    }
  }, [output, language]);

  const downloadMarkdown = useCallback(() => {
    if (!output) return;
    const blob = new Blob([output], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gooseberry-${(form.projectName || "report").toLowerCase().replace(/\s+/g, "-")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [output, form.projectName]);

  const downloadPdf = useCallback(() => {
    if (!output) return;
    try {
      exportGooseBerryPdf({ markdown: output, projectName: form.projectName, mode: form.mode, sourcesUsed, language });
    } catch (err) {
      logger.error("PDF export failed", err);
      toast.error(language === "nl" ? "Kon PDF niet genereren" : "Could not generate PDF");
    }
  }, [output, form.projectName, form.mode, sourcesUsed, language]);

  // ---- Feedback context (passed to every SectionCard) ----
  const feedbackContext = useMemo(
    () => ({
      runId,
      projectName: form.projectName,
      mode: form.mode,
      getFeedbackFor,
      onSubmitted: handleFeedbackSubmitted,
    }),
    [runId, form.projectName, form.mode, getFeedbackFor, handleFeedbackSubmitted]
  );

  // ---- Regenerate with corrections ----
  const onRegenerateWithCorrections = useCallback(async () => {
    if (correctionCount === 0) {
      toast(language === "nl" ? "Voeg eerst een notitie toe aan minstens een 'Niet helemaal'-sectie." : "Add a note to at least one 'Not quite' section before regenerating.");
      return;
    }
    if (!lastPayloadRef.current) {
      toast.error(language === "nl" ? "Geen eerdere run om opnieuw te genereren. Draai eerst de analyse." : "No previous run to regenerate. Please run the analysis first.");
      return;
    }
    setRegenerating(true);
    setRunning(true);
    setProgress(20);
    scrollToElement(outputRef.current, { delay: 60, offset: 90 });
    try {
      const payload = {
        ...lastPayloadRef.current,
        runId,
        corrections: compiledCorrections,
      };
      setProgress(60);
      const data = await feedbackService.regenerateWithCorrections(payload);
      setOutput(data.markdown || "");
      setSourcesUsed(data.sourcesUsed || sourcesUsed || []);
      setVisionUsed(data.visionUsed !== false);
      setModelInfo(data.modelInfo || {});
      setGroundingInfo(data.groundingInfo || {});
      if (data.visionUsed === false) {
        toast(language === "nl" ? "Afbeeldingsanalyse is overgeslagen" : "Image analysis was skipped", {
          description:
            language === "nl"
              ? "De assistant kon je afbeelding niet analyseren, daarom is het rapport alleen gebaseerd op de tekstuele context."
              : "The assistant could not analyze your image, so the report is based on the textual context only.",
          duration: 8000,
        });
      }
      resetFeedback();
      setProgress(100);
      toast.success(language === "nl" ? "Opnieuw gegenereerd met je correcties" : "Regenerated with your corrections");
      scrollToElement(outputRef.current, { delay: 60, offset: 90 });
    } catch (err) {
      reportApiError(err, language === "nl" ? "Kon niet opnieuw genereren" : "Could not regenerate", language);
    } finally {
      setRegenerating(false);
      setRunning(false);
      setTimeout(() => setProgress(0), 600);
    }
  }, [correctionCount, compiledCorrections, runId, sourcesUsed, resetFeedback, language]);

  // ---- Memoized callback bundles to keep child props stable ----
  const contextSetters = useMemo(
    () => ({
      projectName:   (v) => setField("projectName", v),
      targetGroup:   (v) => setField("targetGroup", v),
      societalTheme: (v) => setField("societalTheme", v),
      designPhase:   (v) => setField("designPhase", v),
      assumptions:   (v) => setField("assumptions", v),
    }),
    [setField]
  );

  return (
    <section data-testid="synberry-view" className="pt-8">
      <ViewHeader language={language} setLanguage={setLanguage} onClearAll={clearAll} />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT COLUMN: INPUTS */}
        <div className="space-y-4">
  <ArtifactUploadPanel
    imageDataUrl={imageDataUrl}
    imageMime={imageMime}
    fidelity={form.fidelity}
    setFidelity={(v) => setField("fidelity", v)}
    onImageChange={onImageChange}
    onImageReset={onImageReset}
    language={language}
  />
  <MethodAndModePanel
    methodsAll={derived.methodsAll}
    selectedMethodId={form.selectedMethodId}
    setSelectedMethodId={(v) => setField("selectedMethodId", v)}
    selectedMethod={derived.selectedMethod}
    onAddCustomMethod={(m) => setField("customMethods", [...form.customMethods, m])}
    language={language}
  />
  <ContextPanel
    values={{
      projectName:   form.projectName,
      targetGroup:   form.targetGroup,
      societalTheme: form.societalTheme,
      designPhase:   form.designPhase,
      assumptions:   form.assumptions,
    }}
    set={contextSetters}
    language={language}
  />
  <CustomDatabasePanel
    customText={form.customText}
    setCustomText={(v) => setField("customText", v)}
    customType={form.customType}
    setCustomType={(v) => setField("customType", v)}
    customFileName={form.customFileName}
    setCustomFileName={(v) => setField("customFileName", v)}
    setCustomFileContent={(v) => setField("customFileContent", v)}
    language={language}
  />
  <ResearchMetadataPanel
    results={metadataResults}
    status={metadataStatus}
    loading={metadataLoading}
    onRefresh={refreshMetadata}
    language={language}
  />
  <DatasetPanel
    societalTheme={form.societalTheme}
    targetGroup={form.targetGroup}
    onDatasetsChange={setSelectedDatasets}
    language={language}
  />
  <ModePanel
    mode={form.mode}
    setMode={(v) => setField("mode", v)}
    language={language}
  />
  <RunPanel canRun={canRun} running={running} progress={progress} onRun={onRun} language={language} />
</div>

        {/* RIGHT COLUMN: OUTPUT (tabbed) */}
        <div ref={outputRef} className="space-y-4 lg:sticky lg:top-24 self-start" data-testid="synberry-output-column">
          <GooseBerryOutput
            markdown={output}
            loading={running}
            mode={form.mode}
            projectName={form.projectName}
            sourcesUsed={sourcesUsed}
            fidelity={derived.fidelityLabel}
            visionUsed={visionUsed}
            modelInfo={modelInfo}
            groundingInfo={groundingInfo}
            onCopy={copyMarkdown}
            onDownloadMd={downloadMarkdown}
            onDownloadPdf={downloadPdf}
            feedbackContext={feedbackContext}
            onRegenerateWithCorrections={onRegenerateWithCorrections}
            regenerating={regenerating}
            correctionCount={correctionCount}
            language={language}
          />
          {output && (
            <FinalActions
              onStartNew={() => { setOutput(""); setSourcesUsed([]); setModelInfo({}); setGroundingInfo({}); scrollToTop({ delay: 30 }); }}
              onGoHome={onGoHome}
              language={language}
            />
          )}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-black/10 bg-white p-4 flex justify-end">
        <Button onClick={onGoHome} variant="outline" className="rounded-xl border-black/15" data-testid="synberry-bottom-back-home-button">
          <Home className="h-4 w-4 mr-2" /> {language === "nl" ? "Terug naar home" : "Back to home"}
        </Button>
      </div>
    </section>
  );
}

function ViewHeader({ language, setLanguage, onClearAll }) {
  const isNl = language === "nl";
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[12px] uppercase tracking-[0.18em] text-[#61CBA3] font-medium mb-2">Reflective scanner</p>
          <div className="flex items-center gap-3">
            <img
              src="/brand/synberry-logo.svg"
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              className="h-12 w-12 rounded-xl object-contain ring-1 ring-black/5 bg-[rgba(97,203,163,0.12)]"
              data-testid="gooseberry-view-icon"
            />
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-black break-words">GooseBerry</h1>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="rounded-xl" data-testid="synberry-clear-button">
              <RotateCcw className="h-4 w-4 mr-2" /> {isNl ? "Input wissen" : "Clear all inputs"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{isNl ? "GooseBerry input wissen?" : "Clear GooseBerry inputs?"}</AlertDialogTitle>
              <AlertDialogDescription>
                {isNl
                  ? "Verwijdert opgeslagen context, eigen database, methodeselectie en de geuploade afbeelding uit deze sessie."
                  : "Removes saved context, custom database, method selection, and the uploaded image from this session."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{isNl ? "Annuleren" : "Cancel"}</AlertDialogCancel>
              <AlertDialogAction onClick={onClearAll} className="bg-[#61CBA3] hover:bg-[#4DAF8B]" data-testid="synberry-clear-confirm">
                {isNl ? "Wissen" : "Clear"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <p className="w-full text-black/70 text-sm sm:text-base leading-relaxed">
        <strong className="text-black">
          {isNl
            ? "Upload een afbeelding van je ontwerp, voeg context toe, kies een feedbackmodus en druk op Run. Je krijgt gestructureerde feedback of een dialoog met synthetische gebruikers, gebaseerd op de research metadata die je selecteert of die GooseBerry voor je vindt."
            : "Upload an image of your design, add some context, choose a feedback mode, and press Run. You'll get structured feedback or a synthetic-user dialogue, based on the research metadata you selected (or that we found for you)."}
        </strong>{" "}
        {isNl
          ? "Gebruik dit om op je ontwerp te reflecteren, aannames te herkennen en nieuwe richtingen te ontdekken."
          : "Use this to reflect on your design, identify assumptions, and discover new directions."}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1.5 text-xs text-black/55">
          <Languages className="h-3.5 w-3.5" />
          {isNl ? "Je kunt in Nederlands of Engels typen." : "You can type in Dutch or English."}
        </div>
        <div className="grid grid-cols-2 gap-1 rounded-xl border border-black/10 bg-white p-1">
          <Button
            type="button"
            size="sm"
            variant={language === "nl" ? "default" : "ghost"}
            onClick={() => setLanguage("nl")}
            className={`h-8 rounded-lg px-3 text-xs ${language === "nl" ? "bg-[#61CBA3] text-white hover:bg-[#4DAF8B]" : ""}`}
          >
            Nederlands
          </Button>
          <Button
            type="button"
            size="sm"
            variant={language === "en" ? "default" : "ghost"}
            onClick={() => setLanguage("en")}
            className={`h-8 rounded-lg px-3 text-xs ${language === "en" ? "bg-[#61CBA3] text-white hover:bg-[#4DAF8B]" : ""}`}
          >
            English
          </Button>
        </div>
      </div>
    </div>
  );
}

function FinalActions({ onStartNew, onGoHome, language = "en" }) {
  const isNl = language === "nl";
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid="synberry-output-final-actions">
      <div className="text-sm text-black/70">
        {isNl ? "Als je klaar bent, kun je terug naar het startscherm." : "When you're done, you can return to the home screen."}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" className="rounded-xl border-black/15" onClick={onStartNew} data-testid="synberry-output-start-new-button">
          {isNl ? "Nieuwe run" : "Start new run"}
        </Button>
        <Button onClick={onGoHome} className="rounded-xl bg-[#61CBA3] text-white hover:bg-[#4DAF8B]" data-testid="synberry-output-back-home-button">
          {isNl ? "Terug naar home" : "Back to home"}
        </Button>
      </div>
    </div>
  );
}
